import { Button, Modal, Select, Form, Input, message } from "antd";
import { Package, MapPin, AlertCircle } from "lucide-react";
import { useState, useMemo } from "react";
import { TInventory } from "~/types/inventory";
import { InventoryStatusCode } from "~/utils/constants";
import { useGetLocations } from "~/services/common";
import { useBulkUpdateInventory } from "~/services/inventory/inventory.mutations";
import { useInventoryControlStore } from "./inventory-control.store";

// Extended inventory type to include special markers for "all selected" mode
interface ExtendedInventory extends Partial<TInventory> {
  __allSelected?: boolean;
  __totalCount?: number;
  id: string;
}

interface InventoryBulkUpdateProps {
  selectedInventories: (TInventory | ExtendedInventory)[];
  onUpdateComplete: () => void;
}

export const InventoryBulkUpdate: React.FC<InventoryBulkUpdateProps> = ({
  selectedInventories,
  onUpdateComplete,
}) => {
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [form] = Form.useForm();
  const { data: locations } = useGetLocations();
  const { mutate: bulkUpdateInventory, isPending } = useBulkUpdateInventory();
  const { selectionMode, excludedItems, filters } = useInventoryControlStore();

  // Check if we're in "select all" mode
  const isAllMode = useMemo(() => {
    return (
      selectionMode === "all" ||
      selectedInventories.some(
        (item) => (item as ExtendedInventory).__allSelected
      )
    );
  }, [selectionMode, selectedInventories]);

  // Get the total count of items to update
  const itemCount = useMemo(() => {
    if (isAllMode) {
      const allSelectedItem = selectedInventories.find(
        (item) => (item as ExtendedInventory).__allSelected
      ) as ExtendedInventory;

      return allSelectedItem?.__totalCount || selectedInventories.length - 1;
    }
    return selectedInventories.length;
  }, [isAllMode, selectedInventories]);

  // Get the inventory IDs to update
  const getInventoryUpdateData = () => {
    if (isAllMode) {
      // In "all" mode, we'll get the IDs in a different way through a special API endpoint
      // The backend will need to support this with a filter to get all matching IDs
      return { allMatching: true, filters };
    }

    // For normal mode, extract all inventory IDs
    return { inventoryIds: selectedInventories.map((item) => item.id) };
  };

  const handleOpenModal = () => {
    if (selectedInventories.length === 0 && !isAllMode) {
      message.warning("Please select inventory items to update.");
      return;
    }
    setIsModalVisible(true);
  };

  const handleCloseModal = () => {
    setIsModalVisible(false);
    form.resetFields();
  };

  const handleSubmit = async (values: any) => {
    const { status, locationId, notes, artistName, length, width, height, packingType } = values;

    // Only include fields that have values
    const updateData: any = {
      ...getInventoryUpdateData(),
    };

    if (status) updateData.status = status;
    // Only update location if user explicitly interacted with it:
    // - Empty string "" means user selected "None (Clear Location)" -> send null
    // - Valid UUID means user selected a location -> send that ID
    // - undefined means user didn't touch the field -> don't include in update
    if (locationId === "") {
      updateData.locationId = null; // User explicitly wants to clear location
    } else if (locationId) {
      updateData.locationId = locationId; // User selected a specific location
    }
    // If locationId is undefined, don't include it - keeps existing location
    if (notes) updateData.notes = notes;
    if (artistName) updateData.artistName = artistName;
    if (length) updateData.length = parseFloat(length);
    if (width) updateData.width = parseFloat(width);
    if (height) updateData.height = parseFloat(height);
    if (packingType) updateData.packingType = packingType;

    // Add excluded items if in "all" mode
    if (isAllMode && excludedItems.length > 0) {
      updateData.excludedIds = excludedItems;
    }

    console.log("Submitting bulk update with data:", updateData);
    console.log("Selected inventories:", selectedInventories.length);

    bulkUpdateInventory(updateData, {
      onSuccess: (updatedInventories: TInventory[]) => {
        message.success(
          `Successfully updated ${
            updatedInventories.length || itemCount
          } inventory items!`,
          5
        );
        handleCloseModal();
        onUpdateComplete();
      },
      onError: (error: any) => {
        console.error("Bulk update error:", error);
        const errorMessage = error?.response?.data?.message || error?.message || "Unknown error";
        const statusCode = error?.response?.status;

        let fullError = `Bulk update failed: ${errorMessage}`;
        if (statusCode) {
          fullError += ` (Status: ${statusCode})`;
        }
        if (error?.response?.data?.error) {
          fullError += ` - ${error.response.data.error}`;
        }

        message.error(fullError, 10);
      },
    });
  };

  const getStatusColor = (status: InventoryStatusCode) => {
    switch (status) {
      case InventoryStatusCode.AVAILABLE_LIVE:
        return "#52c41a";
      case InventoryStatusCode.AVAILABLE_ON_HAND:
        return "#1890ff";
      case InventoryStatusCode.NO_POSSESSION:
        return "#fa8c16";
      case InventoryStatusCode.RELEASED:
        return "#722ed1";
      default:
        return "#d9d9d9";
    }
  };

  const statusOptions = [
    {
      label: (
        <div className="flex items-center space-x-2">
          <div
            className="w-3 h-3 rounded-full"
            style={{
              backgroundColor: getStatusColor(
                InventoryStatusCode.AVAILABLE_LIVE
              ),
            }}
          />
          <span>Available Live</span>
        </div>
      ),
      value: InventoryStatusCode.AVAILABLE_LIVE,
    },
    {
      label: (
        <div className="flex items-center space-x-2">
          <div
            className="w-3 h-3 rounded-full"
            style={{
              backgroundColor: getStatusColor(
                InventoryStatusCode.AVAILABLE_ON_HAND
              ),
            }}
          />
          <span>Available On Hand</span>
        </div>
      ),
      value: InventoryStatusCode.AVAILABLE_ON_HAND,
    },
    {
      label: (
        <div className="flex items-center space-x-2">
          <div
            className="w-3 h-3 rounded-full"
            style={{
              backgroundColor: getStatusColor(
                InventoryStatusCode.NO_POSSESSION
              ),
            }}
          />
          <span>No Possession</span>
        </div>
      ),
      value: InventoryStatusCode.NO_POSSESSION,
    },
    {
      label: (
        <div className="flex items-center space-x-2">
          <div
            className="w-3 h-3 rounded-full"
            style={{
              backgroundColor: getStatusColor(InventoryStatusCode.RELEASED),
            }}
          />
          <span>Released</span>
        </div>
      ),
      value: InventoryStatusCode.RELEASED,
    },
  ];

  // Calculate the total number of selected items across all pages
  const totalSelectedCount = useMemo(() => {
    if (selectionMode === "all") {
      return selectedInventories.reduce((total, item) => {
        return total + ((item as ExtendedInventory).__totalCount ?? 0) || 0;
      }, 0);
    }

    return selectedInventories.length;
  }, [selectionMode, selectedInventories]);

  if (selectedInventories.length === 0 && !isAllMode) {
    return null;
  }

  return (
    <div className="bg-white rounded-lg border shadow-sm">
      {/* Floating notification for selections across pages */}
      {totalSelectedCount > 0 && (
        <div className="fixed bottom-4 right-4 bg-blue-600 text-white py-2 px-4 rounded-full shadow-lg z-50 flex items-center space-x-2">
          <span className="font-medium">
            {totalSelectedCount} items selected
          </span>
          <Button
            type="text"
            size="small"
            onClick={handleOpenModal}
            className="text-white hover:text-blue-200 border-white hover:border-blue-200"
          >
            Bulk Update
          </Button>
        </div>
      )}

      {/* Search Bar */}
      <div className="p-4 border-b">
        <h2 className="text-lg font-semibold text-gray-800">
          Inventory Management
        </h2>
        <p className="text-sm text-gray-500">
          Manage your inventory items, update statuses, and track changes.
        </p>
      </div>

      {/* Inventory List */}
      <div className="p-4">
        {selectedInventories.length === 0 ? (
          <p className="text-center text-gray-500 py-4">
            No inventory items selected. Use the search to find items.
          </p>
        ) : (
          <div>
            {/* Selected Items Header */}
            <div className="flex items-center justify-between mb-4">
              <div>
                <h3 className="text-md font-medium text-gray-700">
                  Selected Inventory Items
                </h3>
                <p className="text-sm text-gray-500">
                  {isAllMode
                    ? `All items matching current filters (${itemCount} total)`
                    : `${selectedInventories.length} item${
                        selectedInventories.length !== 1 ? "s" : ""
                      } selected`}
                </p>
              </div>
              <Button
                type="primary"
                onClick={handleOpenModal}
                className="bg-blue-600 hover:bg-blue-700"
              >
                Bulk Update
              </Button>
            </div>

            {/* Inventory Table */}
            <div className="bg-white rounded-lg shadow overflow-hidden">
              <div className="border-b">
                <div className="grid grid-cols-4 gap-4 py-3 px-4 text-xs font-medium text-gray-500 bg-gray-50">
                  <div className="flex items-center space-x-2">
                    <Package className="text-gray-400" size={16} />
                    <span>Item</span>
                  </div>
                  <div className="text-center">
                    <span>Status</span>
                  </div>
                  <div className="text-center">
                    <span>Location</span>
                  </div>
                  <div className="text-center">
                    <span>Actions</span>
                  </div>
                </div>
              </div>
              <div>
                {selectedInventories.map((item) => (
                  <div
                    key={item.id}
                    className="grid grid-cols-4 gap-4 py-3 px-4 text-sm border-b"
                  >
                    <div className="flex items-center space-x-2">
                      <Package className="text-gray-600" size={20} />
                      <div>
                        <div className="font-medium text-gray-800">
                          {(item as TInventory).inventoryRef}
                        </div>
                        <div className="text-gray-500 text-xs">
                          {(item as TInventory).title || "Untitled"}
                        </div>
                      </div>
                    </div>
                    <div className="text-center">
                      <div
                        className="w-3 h-3 rounded-full mx-auto"
                        style={{
                          backgroundColor: getStatusColor(
                            (item as TInventory).status
                          ),
                        }}
                      />
                    </div>
                    <div className="text-center">
                      <span className="text-gray-800 text-sm">
                        {item.locationId
                          ? locations?.items.find(
                              (loc) => loc.id === item.locationId
                            )?.name || "Unknown"
                          : "No location"}
                      </span>
                    </div>
                    <div className="text-center">
                      <Button
                        type="link"
                        size="small"
                        className="text-blue-600 hover:text-blue-500"
                        onClick={() => {
                          // Handle individual update
                        }}
                      >
                        Update
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Bulk Update Modal */}
      <Modal
        title={
          <div className="flex items-center space-x-2">
            <Package className="text-blue-600" size={20} />
            <span>Bulk Update Inventory</span>
          </div>
        }
        open={isModalVisible}
        onCancel={handleCloseModal}
        footer={null}
        width={600}
      >
        <div className="mb-4 p-3 bg-blue-50 rounded-lg border border-blue-200">
          <div className="flex items-start space-x-2">
            <AlertCircle className="text-blue-600 mt-0.5" size={16} />
            <div>
              <p className="text-sm text-blue-800 font-medium">
                {isAllMode ? (
                  <span>
                    Updating all {itemCount} inventory items matching current
                    filters
                  </span>
                ) : (
                  <span>
                    Updating {selectedInventories.length} inventory item
                    {selectedInventories.length !== 1 ? "s" : ""}
                  </span>
                )}
              </p>
              <p className="text-xs text-blue-600 mt-1">
                Only the fields you specify will be updated. Leave fields empty
                to keep existing values.
              </p>
            </div>
          </div>
        </div>

        {/* Selected Items Preview */}
        <div className="mb-6">
          <h4 className="text-sm font-medium text-gray-700 mb-2">
            Selected Items:
          </h4>
          <div className="max-h-32 overflow-y-auto border rounded-lg p-2 bg-gray-50">
            {isAllMode ? (
              <div className="text-xs text-blue-600 py-1 font-medium">
                All items matching current filters ({itemCount} total)
                {excludedItems.length > 0 &&
                  ` (${excludedItems.length} items excluded)`}
              </div>
            ) : (
              <>
                {selectedInventories.slice(0, 5).map((item) => (
                  <div key={item.id} className="text-xs text-gray-600 py-1">
                    {(item as TInventory).inventoryRef} -{" "}
                    {(item as TInventory).title || "Untitled"}
                  </div>
                ))}
                {selectedInventories.length > 5 && (
                  <div className="text-xs text-gray-500 py-1">
                    +{selectedInventories.length - 5} more items...
                  </div>
                )}
              </>
            )}
          </div>
        </div>

        <Form form={form} layout="vertical" onFinish={handleSubmit}>
          <Form.Item
            label={
              <div className="flex items-center space-x-2">
                <div className="w-3 h-3 rounded-full bg-gray-400" />
                <span>Status</span>
              </div>
            }
            name="status"
          >
            <Select
              placeholder="Select new status (optional)"
              allowClear
              options={statusOptions}
            />
          </Form.Item>

          <Form.Item
            label={
              <div className="flex items-center space-x-2">
                <MapPin className="text-gray-600" size={14} />
                <span>Location</span>
              </div>
            }
            name="locationId"
          >
            <Select
              placeholder="Select new location (optional)"
              allowClear
              showSearch
              optionFilterProp="children"
            >
              <Select.Option value="">None (Clear Location)</Select.Option>
              {locations?.items.map((location: any) => (
                <Select.Option key={location.id} value={location.id}>
                  {location.name}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item label="Artist Name" name="artistName">
            <Input
              placeholder="Update artist name (optional)"
            />
          </Form.Item>

          <div className="grid grid-cols-3 gap-4">
            <Form.Item label="Length (in)" name="length">
              <Input
                type="number"
                placeholder="Length"
                step="0.1"
              />
            </Form.Item>

            <Form.Item label="Width (in)" name="width">
              <Input
                type="number"
                placeholder="Width"
                step="0.1"
              />
            </Form.Item>

            <Form.Item label="Height (in)" name="height">
              <Input
                type="number"
                placeholder="Height"
                step="0.1"
              />
            </Form.Item>
          </div>

          <Form.Item label="Packing Type" name="packingType">
            <Select
              placeholder="Select packing type (optional)"
              allowClear
              options={[
                { label: "Loose", value: "LOOSE" },
                { label: "Slipcase", value: "SLIPCASE" },
                { label: "CB Fold", value: "CB_FOLD" },
                { label: "Shadow Box", value: "SHADOW_BOX" },
                { label: "Floating Shadow Box", value: "FLOATING_SHADOW_BOX" },
                { label: "Poly Only", value: "POLY_ONLY" },
                { label: "Travel Frame", value: "TRAVEL_FRAME" },
                { label: "Crate", value: "CRATE" },
                { label: "Commercial Bin", value: "COMMERCIAL_BIN" },
                { label: "Unknown Packaging", value: "UNKNOWN_PACKAGING" },
                { label: "Box", value: "BOX" },
                { label: "Custom", value: "CUSTOM" },
                { label: "T-Frame", value: "T_Frame" },
                { label: "Blanket Wrapped", value: "BLANKET_WRAPPED" },
              ]}
            />
          </Form.Item>

          <Form.Item label="Notes" name="notes">
            <Input.TextArea
              placeholder="Add notes about this update (optional)"
              rows={3}
            />
          </Form.Item>

          <div className="flex justify-end space-x-3 pt-4 border-t">
            <Button onClick={handleCloseModal}>Cancel</Button>
            <Button
              type="primary"
              htmlType="submit"
              loading={isPending}
              className="bg-blue-600 hover:bg-blue-700"
            >
              Update {isAllMode ? "All Selected" : itemCount} Item
              {itemCount !== 1 ? "s" : ""}
            </Button>
          </div>
        </Form>
      </Modal>
    </div>
  );
};
